import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Icon from "../Icon";
import useFocoModal from "../../hooks/useFocoModal";
import { Avatar } from "../Atoms";
import * as api from "../../api";
import SelectorUsuario, { AvatarOdoo } from "../widgets/SelectorUsuario";
import SelectorEtapa from "../widgets/SelectorEtapa";
import { mapaColoresEtapas, colorEtapa as colorPorNombre, etapaEnCurso, etapaResuelta } from "../../utils";
import { clicable } from "../ClicableTeclado.jsx";

// Detalle de una incidencia: adjuntos a la izquierda, ficha a la derecha.


const fecha = iso => iso
  ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit",
                                            hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid" })
  : "—";

// Mismo formato que el contador de la cabecera: en miniatura el tiempo va en
// horas y minutos. Los segundos corriendo no se usan para nada aqui y el
// cronometro al segundo esta en el panel de inicio.
const corto = ms => {
  const min = Math.max(0, Math.floor(ms / 60000));
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:`
       + `${String(min % 60).padStart(2, "0")}`;
};

const tamano = b => b > 1048576 ? `${(b / 1048576).toFixed(1)} MB`
                  : b > 1024    ? `${Math.round(b / 1024)} KB`
                                : `${b} B`;

const ES_PDF   = a => /pdf$/i.test(a.tipo || "");
// CSV y texto plano se pueden pintar tal cual. El resto de ofimatica (xlsx,
// docx) no lo renderiza ningun navegador: esos se descargan.
const ES_TEXTO = a => /^text\/|csv$|json$/i.test(a.tipo || "") || /\.(csv|txt|log)$/i.test(a.nombre || "");
const PREVISUALIZABLE = a => a.imagen || ES_PDF(a) || ES_TEXTO(a);

// En helpdesk_mgmt el ticket que nace de un correo guarda ese correo en la
// descripción Y como primer mensaje del hilo. Mostrar los dos duplica el texto
// más largo de la ficha.
const soloTexto = html => String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
const esElMismo = (a, b) => {
  const x = soloTexto(a), y = soloTexto(b);
  if (!x || !y) return false;
  return x === y || x.startsWith(y.slice(0, 160)) || y.startsWith(x.slice(0, 160));
};

export default function DetalleTicketModal({ ticket, adjuntos, mensajes = [], cargando, usuarios, etapas,
                                             onActualizar, onResponder, onClose, showToast,
                                             crono, agendaTiposData = [], onIrAInicio }) {
  const [guardando, setGuardando] = useState(false);
  const [urls, setUrls]     = useState({});    // attId → object URL
  const [activo, setActivo] = useState(null);  // adjunto que se está viendo
  const [textos, setTextos] = useState({});   // attId → contenido, para CSV y texto
  const [completos, setCompletos] = useState({});
  const [giro, setGiro] = useState(0);        // grados, en pasos de 90
  const [verFirmas, setVerFirmas] = useState(false);
  const [zoom, setZoom] = useState(1);        // 1 = encajada en el visor
  const [natural, setNatural] = useState({ w: 0, h: 0 });   // tamano real del fichero
  const visor = useRef(null);
  const [caja, setCaja] = useState({ w: 0, h: 0 });
  // Menu del boton de tiempo, y el tick que hace correr su contador.
  const [menuTiempo, setMenuTiempo] = useState(false);
  const [ahora, setAhora] = useState(Date.now());
  const cajaTiempo = useRef(null);

  // Las imagenes de firma se detectan en el servidor por hash repetido. Se
  // ocultan por defecto: la franja del logo llega en cada correo y en algunas
  // incidencias es la mayoria de la tira de adjuntos.
  const firmas = adjuntos.filter(a => a.firma);
  const visibles = verFirmas ? adjuntos : adjuntos.filter(a => !a.firma);
  // Si lo unico adjunto son firmas, el visor arrancaria vacio y robando media
  // pantalla al hilo. En ese caso no se pinta: queda el boton de las firmas
  // sobre la ficha y el visor aparece solo si alguien lo pulsa.
  const hayVisor = visibles.length > 0;

  // Tamano con el que se pinta la imagen.
  //
  // Con maxWidth/maxHeight no bastaba: esas propiedades solo LIMITAN, asi que
  // al pasar del 100 % el limite subia pero la imagen ya estaba a su tamano
  // natural y no crecia. Hay que calcular el ancho real: se mide el fichero al
  // cargarlo, se saca la escala que lo encaja en el visor —cruzando alto y
  // ancho si esta girado— y esa escala se multiplica por el zoom.
  const cajaW = (giro % 180 ? caja.h : caja.w) - 28;
  const cajaH = (giro % 180 ? caja.w : caja.h) - 28;
  const ajuste = natural.w && cajaW > 0
    ? Math.min(cajaW / natural.w, cajaH / natural.h)
    : 1;
  const anchoImagen = natural.w ? Math.round(natural.w * ajuste * zoom) : null;
  const [borrador, setBorrador] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [desplegado, setDesplegado] = useState(false);

  // Los binarios se piden como blob y se pintan desde un object URL: un <img
  // src> no puede mandar la cabecera Authorization, y meter el token en la URL
  // lo dejaría escrito en logs e historial.
  useEffect(() => {
    let vivo = true;
    const creadas = [];
    setActivo(visibles.find(PREVISUALIZABLE) || visibles[0] || null);
    (async () => {
      for (const a of visibles.filter(PREVISUALIZABLE)) {
        try {
          const url = await api.getHelpdeskAdjuntoUrl(ticket.id, a.id);
          if (!vivo) { URL.revokeObjectURL(url); return; }
          creadas.push(url);
          setUrls(p => ({ ...p, [a.id]: url }));
          if (ES_TEXTO(a)) {
            const txt = await (await fetch(url)).text();
            if (vivo) setTextos(p => ({ ...p, [a.id]: txt.slice(0, 200000) }));
          }
        } catch { /* un adjunto que falle no debe romper el detalle */ }
      }
    })();
    return () => { vivo = false; creadas.forEach(URL.revokeObjectURL); setUrls({}); setTextos({}); };
  }, [adjuntos, verFirmas, ticket.id]); // eslint-disable-line

  // Todos los mensajes arrancan recortados, incluido el primero: la firma
  // corporativa y el aviso legal ocupan mas que el correo.
  useEffect(() => { setCompletos({}); }, [mensajes]);
  useEffect(() => { setDesplegado(false); setBorrador(""); }, [ticket.id]);

  // Contacto y asignado, en un solo viaje al abrir la ficha.
  const [, refrescarAvatares] = useState(0);
  useEffect(() => {
    const tareas = [];
    if (ticket.contacto?.id) tareas.push(api.precargarAvatares("res.partner", [ticket.contacto.id]));
    if (ticket.asignado?.id) tareas.push(api.precargarAvatares("res.users", [ticket.asignado.id]));
    if (tareas.length) Promise.all(tareas).then(() => refrescarAvatares(n => n + 1));
  }, [ticket.contacto?.id, ticket.asignado?.id]);

  // Cambiar de adjunto reinicia el giro: heredarlo del anterior desorienta.
  useEffect(() => { setGiro(0); setZoom(1); setNatural({ w: 0, h: 0 }); }, [activo?.id]);

  // Al girar 90 grados, el alto y el ancho se intercambian. Sin medir la caja
  // no hay forma de encajar la imagen: un maxWidth del 100% se calcula sobre el
  // ancho sin rotar y la foto se sale del visor.
  useEffect(() => {
    const el = visor.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const medir = () => setCaja({ w: el.clientWidth, h: el.clientHeight });
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hayVisor]);

  // El modal ocupa casi toda la pantalla: buscar la X cansa. Escape, la trampa
  // de foco y la devolucion del foco los pone useFocoModal.
  const cajaRef = useRef(null);
  useFocoModal(cajaRef, true, onClose);

  // El cronometro de ESTA incidencia, si es el que corre. El tick vive aqui y
  // solo mientras corre: sin esto el contador del boton se quedaria congelado.
  // Cada 15 s basta, que el contador va en minutos.
  const cronoActivo = crono?.activo
    && String(crono.activo.incidencia_id) === String(ticket.id) ? crono.activo : null;
  useEffect(() => {
    if (!cronoActivo) return;
    const id = setInterval(() => setAhora(Date.now()), 15000);
    return () => clearInterval(id);
  }, [cronoActivo?.id]); // eslint-disable-line

  // Al pararse el cronometro el menu se queda sin acciones que ofrecer.
  useEffect(() => { if (!cronoActivo) setMenuTiempo(false); }, [cronoActivo]);

  useEffect(() => {
    if (!menuTiempo) return;
    const fuera = e => {
      if (cajaTiempo.current && !cajaTiempo.current.contains(e.target)) setMenuTiempo(false);
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [menuTiempo]);

  const descargar = async (a) => {
    try {
      const url = urls[a.id] || await api.getHelpdeskAdjuntoUrl(ticket.id, a.id);
      const el = document.createElement("a");
      el.href = url; el.download = a.nombre;
      document.body.appendChild(el); el.click(); el.remove();
    } catch (e) { showToast(e.message || "No se pudo descargar el adjunto", "error"); }
  };

  const cambiar = async (cambios) => {
    setGuardando(true);
    const t = await onActualizar(cambios);
    setGuardando(false);
    return t;
  };

  // Empezar a cronometrar una incidencia es tambien hacerse cargo de ella: se
  // asigna a quien la arranca y pasa a la etapa de "en curso". Dos cautelas,
  // porque las dos escriben en Odoo y son visibles para todo el equipo:
  //
  //   · Si ya esta asignada a OTRA persona no se le quita. Quitarle el ticket a
  //     un compañero en silencio, solo por mirar cuanto tardas, es peor que
  //     dejarlo como esta; se avisa y se sigue cronometrando.
  //   · La etapa solo AVANZA. Arrancar el cronometro sobre algo ya resuelto no
  //     puede devolverlo a "en curso" y reabrir trabajo cerrado.
  const hacerseCargo = async () => {
    const cambios = {};
    if (!ticket.asignado) cambios.user_id = "yo";

    const destino = etapaEnCurso(etapas);
    const actual  = etapas.find(e => String(e.id) === String(ticket.etapa?.id));
    if (destino && String(destino.id) !== String(ticket.etapa?.id)
        && (!actual || (actual.sequence ?? 0) < (destino.sequence ?? 0)))
      cambios.stage_id = destino.id;

    if (!Object.keys(cambios).length) return;
    // No se anade ningun aviso propio: `onActualizar` ya avisa —del exito y del
    // fallo— y la ficha refresca ASIGNADO y ETAPA a la vista, que es mejor
    // prueba de lo que ha cambiado que un mensaje que se va en tres segundos.
    await cambiar(cambios);
  };

  // Resolver desde el boton de tiempo: el trabajo hecho se anota en el cuaderno
  // y la incidencia se da por resuelta en Odoo, en un solo gesto.
  const resolver = async () => {
    if (!cronoActivo) return;
    // El tiempo PRIMERO, por lo mismo que al arrancar: si luego falla Odoo, el
    // trabajo ya esta anotado y la etapa se cambia a mano con un clic; al reves
    // se pierde el tiempo contado. `parar` ya avisa de que quedo anotado.
    const anotado = await crono.parar(cronoActivo.id);
    if (!anotado) return;

    const cambios = {};
    // La resuelve quien la ha cronometrado —cada cronometro es de su dueño—,
    // pero si ya estaba asignada a OTRA persona no se le quita: mismo criterio
    // que al hacerse cargo.
    if (!ticket.asignado) cambios.user_id = "yo";

    const destino = etapaResuelta(etapas);
    const actual  = etapas.find(e => String(e.id) === String(ticket.etapa?.id));
    // La etapa solo AVANZA: si ya estaba cerrada o cancelada, traerla de vuelta
    // a «Resuelta» seria reabrir algo que alguien archivo a proposito.
    if (!destino)
      showToast("El tiempo queda anotado en el cuaderno, pero ninguna etapa se "
                + "llama «Resuelta»: cambiala a mano.", "warn");
    else if (String(destino.id) !== String(ticket.etapa?.id)
             && (!actual || (actual.sequence ?? 0) < (destino.sequence ?? 0)))
      cambios.stage_id = destino.id;

    if (Object.keys(cambios).length) await cambiar(cambios);
  };

  const etiqueta = { fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em",
                     color:"var(--text3)", marginBottom:3 };

  // El recorte de firmas y citas es por patrones, no es infalible: siempre se
  // puede ver el correo tal cual llegó.
  const VerOriginal = ({ clave, activo }) => (
    <button className="btn" onClick={() => setCompletos(p => ({ ...p, [clave]: !p[clave] }))}
      style={{ marginTop:6, padding:"2px 8px", borderRadius:6, border:"1px solid var(--border)",
               background:"transparent", cursor:"pointer", fontSize:10, color:"var(--text3)",
               fontFamily:"var(--font)" }}>
      {activo ? "Ocultar firma y texto citado" : "Ver correo completo (firma y texto citado)"}
    </button>
  );

  // Mismo criterio que el tablero: el color sale de la lista completa de
  // etapas, no del nombre suelto, para que ninguna se repita.
  // Sin visor no hay nada que llenar la mitad izquierda: el modal se queda en
  // una columna de lectura y crece solo lo que pida el hilo, hasta el alto de
  // la pantalla. A pantalla completa solo se va cuando hay adjuntos que ver.
  const compacto = !hayVisor;

  const cEtapa = mapaColoresEtapas(etapas)[ticket.etapa?.id]
                 || colorPorNombre(ticket.etapa?.nombre) || "#8b5cf6";

  // Si el primer mensaje del hilo es la descripción, no se repite: el hilo ya
  // la muestra, y además con autor y fecha.
  // Se compara contra el correo SIN recortar: si se comparase con el recortado,
  // un mensaje muy podado nunca coincidiría y la descripción saldría repetida.
  const duplicada = mensajes.length > 0 &&
    esElMismo(ticket.descripcion_completa || ticket.descripcion,
              mensajes[0].cuerpo_completo || mensajes[0].cuerpo);

  const contenido = (
    <div onClick={onClose}
      style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.5)", backdropFilter:"blur(3px)",
               zIndex:400, display:"flex", alignItems:"center", justifyContent:"center", padding:14 }}>
      <div ref={cajaRef} role="dialog" aria-modal="true"
        aria-label={"Incidencia " + (ticket.numero || "") + (ticket.titulo ? ": " + ticket.titulo : "")}
        onClick={e => e.stopPropagation()}
        style={{ background:"var(--surface)", borderRadius:16, boxShadow:"var(--shadow-lg)",
                 border:"1px solid var(--border)",
                 width: compacto ? "min(980px,96vw)" : "100%",
                 height: compacto ? "auto" : "100%",
                 maxHeight:"100%", maxWidth: compacto ? undefined : 1920,
                 display:"flex", flexDirection:"column", overflow:"hidden" }}>

        {/* Cabecera: identidad, estado y personas. Todo el contexto de la
            incidencia vive aquí para dejar el panel derecho solo al hilo. */}
        <div style={{ padding:"16px 22px 13px", borderBottom:"1px solid var(--border2)", flexShrink:0,
                      borderTop:`3px solid ${cEtapa}` }}>
          <div style={{ display:"flex", alignItems:"flex-start", gap:12, marginBottom:12 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:11, fontFamily:"var(--mono)", color:"var(--text3)", marginBottom:3 }}>
                {ticket.numero}
              </div>
              <div style={{ fontSize:18, fontWeight:800, letterSpacing:"-.02em", lineHeight:1.25 }}>
                {ticket.titulo}
              </div>
            </div>
            <button aria-label="Cerrar incidencia" className="btn" onClick={onClose}
              style={{ padding:7, borderRadius:9, border:"1px solid var(--border)",
                       background:"var(--surface2)", cursor:"pointer", flexShrink:0 }}>
              <Icon name="x-lg" size={14} color="var(--text3)"/>
            </button>
          </div>

          <div style={{ display:"flex", alignItems:"flex-end", gap:14, flexWrap:"wrap" }}>

            {/* Etapa: el select lleva el color de su propia etapa */}
            <div>
              <div style={etiqueta}>Etapa</div>
              <SelectorEtapa etapas={etapas} valor={ticket.etapa?.id} disabled={guardando}
                onChange={id => cambiar({ stage_id: id })}/>
            </div>

            {/* Contacto: quien reporta */}
            {ticket.contacto && (
              <div>
                <div style={etiqueta}>Contacto</div>
                <div style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 11px 4px 4px",
                              borderRadius:20, background:"var(--surface2)", border:"1px solid var(--border)" }}>
                  <AvatarOdoo modelo="res.partner" id={ticket.contacto.id} nombre={ticket.contacto.nombre}/>
                  <span style={{ fontSize:11.5, fontWeight:600, color:"var(--text)" }}>
                    {ticket.contacto.nombre}
                  </span>
                </div>
              </div>
            )}

            {/* Empezar a cronometrarla desde aqui. El caso real es este: estas
                mirando la incidencia, no el panel de inicio.

                Con el cronometro en marcha el boton NO para nada de un clic:
                abre el menu. Es el mismo criterio que el contador de la
                cabecera —un roce cortaria un tramo de trabajo real— y ademas
                aqui hay tres salidas distintas y solo una es la de siempre. */}
            {crono && crono.disponible !== false && (() => {
              // El tipo se resuelve por su origen, no por su nombre: cada uno
              // llama a los suyos como quiere.
              const tipo = agendaTiposData.find(t => !t.archived && t.origen === "incidencia");
              const activa = crono.activo
                && String(crono.activo.incidencia_id) === String(ticket.id);

              const empezar = async () => {
                if (!tipo) {
                  showToast("Ningún tipo de tu cuaderno tiene origen «Incidencia». "
                            + "Configúralo en el Cuaderno.", "warn");
                  return;
                }
                // El cronometro primero: es dato nuestro y es lo que se ha
                // pedido. Si arranca pero Odoo falla al asignar, queda el
                // tiempo contado y la incidencia sin tocar, que se arregla
                // con dos clics; al reves se pierde el tiempo.
                const r = await crono.iniciar({
                  type: tipo.name, description: ticket.titulo,
                  incidencia_id: ticket.id, incidencia_ref: String(ticket.numero),
                  incidencia_titulo: ticket.titulo,
                  // Quien la pidio, no el equipo de Odoo: ese es el del
                  // helpdesk y sale siempre el mismo. Aqui SI hay correo,
                  // que es la clave exacta contra la plantilla.
                  incidencia_contacto: ticket.contacto?.nombre || null,
                  incidencia_email: ticket.email || null,
                });
                if (r) await hacerseCargo();
              };

              return (
                <div ref={cajaTiempo} style={{ position:"relative" }}>
                  <div style={etiqueta}>Tiempo</div>
                  <button className="btn" disabled={crono.guardando}
                    onClick={() => { if (activa) setMenuTiempo(m => !m); else empezar(); }}
                    title={activa ? "Pausar, cambiar de tarea o resolver" : "Empezar a cronometrar"}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px",
                      borderRadius:20, cursor:"pointer", fontFamily:"var(--font)",
                      fontSize:11.5, fontWeight:600,
                      border:"1px solid " + (activa ? "#f59e0b" : "var(--border)"),
                      background: activa ? "#fffbeb" : "var(--surface2)",
                      color: activa ? "#b45309" : "var(--text2)" }}>
                    <Icon name={activa ? "hourglass-split" : "play-fill"} size={13}
                      color={activa ? "#b45309" : "var(--ok-text)"}/>
                    {activa
                      ? <span style={{ fontVariantNumeric:"tabular-nums" }}>
                          {corto(crono.msDe(crono.activo, ahora))}
                        </span>
                      : "Empezar"}
                    {activa && <Icon name="chevron-down" size={9} color="#b45309"/>}
                  </button>

                  {activa && menuTiempo && (
                    <div style={{ position:"absolute", left:0, top:"calc(100% + 6px)", zIndex:60,
                      minWidth:236, background:"var(--surface)", border:"1px solid var(--border)",
                      borderRadius:11, boxShadow:"var(--shadow-lg)", padding:7,
                      display:"flex", flexDirection:"column", gap:2 }}>
                      <OpcionTiempo icono="pause-circle-fill" color="#f59e0b" titulo="Pausar"
                        pie="El tiempo se guarda y sigue sin anotar"
                        disabled={crono.guardando}
                        onClick={async () => { setMenuTiempo(false); await crono.pausar(crono.activo.id); }}/>
                      <OpcionTiempo icono="arrow-repeat" color="var(--accent)" titulo="Cambiar de tarea"
                        pie="Pausa esta y te lleva al panel de inicio"
                        disabled={crono.guardando}
                        onClick={async () => {
                          setMenuTiempo(false);
                          if (await crono.pausar(crono.activo.id)) onIrAInicio?.();
                        }}/>
                      <OpcionTiempo icono="journal-text" color="var(--text3)" titulo="Terminar y anotar"
                        pie="Cierra el tiempo en el cuaderno y deja la incidencia como esta"
                        disabled={crono.guardando}
                        onClick={async () => { setMenuTiempo(false); await crono.parar(crono.activo.id); }}/>
                      <OpcionTiempo icono="check-circle-fill" color="var(--ok-text)" titulo="Resolver"
                        pie="Anota el tiempo en el cuaderno y da la incidencia por resuelta"
                        disabled={crono.guardando || guardando}
                        onClick={() => { setMenuTiempo(false); resolver(); }}/>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Asignado: quien lo resuelve, editable */}
            <div>
              <div style={etiqueta}>Asignado</div>
              <SelectorUsuario usuarios={usuarios} valor={ticket.asignado?.id}
                nombre={ticket.asignado?.nombre} disabled={guardando || !usuarios.length}
                onChange={id => cambiar({ user_id: id })}/>
            </div>

            {guardando && (
              <span style={{ fontSize:11, color:"var(--text3)", paddingBottom:6 }}>Guardando…</span>
            )}

            {/* El resto es contexto, no acción, pero también rotulado */}
            <div style={{ marginLeft:"auto", display:"flex", gap:16, flexWrap:"wrap" }}>
              {[
                ["Equipo",      ticket.equipo?.nombre],
                ["Categoría",   ticket.categoria?.nombre],
                ["Canal",       ticket.canal?.nombre],
                ["Creada",      fecha(ticket.creado)],
                ["Actualizada", fecha(ticket.editado)],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k}>
                  <div style={etiqueta}>{k}</div>
                  <div style={{ fontSize:11.5, color:"var(--text2)", paddingBottom:5 }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="stack-mob" style={{ flex:1, display:"flex", minHeight:0 }}>

          {/* Panel de adjuntos */}
          {hayVisor && (
            <div className="pane-visor" style={{ flex:"1 1 50%", minWidth:0, borderRight:"1px solid var(--border2)",
                          display:"flex", flexDirection:"column", background:"var(--bg)" }}>
              <div ref={visor}
                style={{ flex:1, minHeight:0, display:"flex", padding:14, overflow:"auto",
                         // Ampliada, la imagen debe recorrerse desde su esquina;
                         // centrada solo mientras cabe entera.
                         alignItems: zoom > 1 ? "flex-start" : "center",
                         justifyContent: zoom > 1 ? "flex-start" : "center" }}>
                {!activo || !PREVISUALIZABLE(activo)
                  ? <div style={{ fontSize:12, color:"var(--text3)", textAlign:"center", lineHeight:1.6 }}>
                      <Icon name="file-text" size={26} color="var(--text3)"/><br/>
                      {activo ? activo.nombre : ""}<br/>
                      <span style={{ fontSize:11 }}>
                        Este tipo de fichero no se puede ver en el navegador. Descárgalo para abrirlo.
                      </span>
                    </div>
                  : !urls[activo.id]
                    ? <div style={{ fontSize:12, color:"var(--text3)" }}>Cargando adjunto…</div>
                    : activo.imagen
                      ? <img src={urls[activo.id]} alt={activo.nombre}
                          onLoad={e => setNatural({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
                          style={{ width: anchoImagen ? `${anchoImagen}px` : "auto",
                                   height:"auto", maxWidth:"none", maxHeight:"none",
                                   // El visor es un contenedor flex y sus hijos
                                   // se encogen por defecto: sin esto, la imagen
                                   // ampliada se comprimia otra vez al ancho
                                   // disponible y el zoom no se notaba.
                                   flexShrink:0,
                                   borderRadius:8, boxShadow:"var(--shadow-sm)",
                                   cursor: zoom >= 4 ? "zoom-out" : "zoom-in",
                                   transform:`rotate(${giro}deg)`, transition:"transform .2s" }}
                          onClick={() => setZoom(z => (z >= 4 ? 1 : +(z + 0.5).toFixed(1)))}/>
                      : ES_TEXTO(activo)
                        ? <pre style={{ width:"100%", height:"100%", margin:0, overflow:"auto",
                                        background:"var(--surface)", borderRadius:8, padding:14,
                                        fontSize:11.5, lineHeight:1.5, fontFamily:"var(--mono)",
                                        color:"var(--text)", whiteSpace:"pre-wrap", overflowWrap:"anywhere" }}>
                            {textos[activo.id] ?? "Cargando…"}
                          </pre>
                        : <iframe title={activo.nombre} src={urls[activo.id]}
                            style={{ width:"100%", height:"100%", border:"none", borderRadius:8,
                                     background:"var(--surface)" }}/>}
              </div>

              <div style={{ borderTop:"1px solid var(--border2)", padding:"9px 12px", flexShrink:0,
                            background:"var(--surface)" }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <span style={etiqueta}>Adjuntos · {visibles.length}</span>
                  {firmas.length > 0 && (
                    <button className="btn" onClick={() => setVerFirmas(v => !v)}
                      title="Las imágenes de firma se detectan por repetirse en muchas incidencias"
                      style={{ padding:"3px 9px", borderRadius:7, border:"1px solid var(--border)",
                               background: verFirmas ? "var(--accent-bg)" : "transparent",
                               cursor:"pointer", fontSize:10, fontFamily:"var(--font)",
                               color: verFirmas ? "var(--accent)" : "var(--text3)" }}>
                      {verFirmas ? "Ocultar firmas" : `${firmas.length} de firma`}
                    </button>
                  )}

                  {activo?.imagen && (
                    <div style={{ marginLeft:"auto", display:"flex", gap:4, alignItems:"center" }}>
                      <button aria-label="Reducir" className="btn" title="Reducir" disabled={zoom <= 0.5}
                        onClick={() => setZoom(z => Math.max(0.5, +(z - 0.5).toFixed(1)))}
                        style={{ padding:"4px 7px", borderRadius:7, border:"1px solid var(--border)",
                                 background:"var(--surface2)", cursor:"pointer", lineHeight:0,
                                 opacity: zoom <= 0.5 ? .4 : 1 }}>
                        <Icon name="dash" size={12} color="var(--text3)"/>
                      </button>

                      <button className="btn" title="Ajustar al visor" onClick={() => setZoom(1)}
                        style={{ padding:"4px 6px", borderRadius:7, border:"1px solid var(--border)",
                                 background: zoom === 1 ? "var(--surface2)" : "var(--accent-bg)",
                                 cursor:"pointer", fontSize:10, fontFamily:"var(--mono)", minWidth:38,
                                 color: zoom === 1 ? "var(--text3)" : "var(--accent)" }}>
                        {Math.round(zoom * 100)}%
                      </button>

                      <button aria-label="Ampliar" className="btn" title="Ampliar" disabled={zoom >= 4}
                        onClick={() => setZoom(z => Math.min(4, +(z + 0.5).toFixed(1)))}
                        style={{ padding:"4px 7px", borderRadius:7, border:"1px solid var(--border)",
                                 background:"var(--surface2)", cursor:"pointer", lineHeight:0,
                                 opacity: zoom >= 4 ? .4 : 1 }}>
                        <Icon name="plus-lg" size={12} color="var(--text3)"/>
                      </button>

                      <span style={{ width:6 }}/>

                      {[["izquierda", -90], ["derecha", 90]].map(([lado, paso]) => (
                        <button aria-label={`Girar a la ${lado}`} key={lado} className="btn" title={`Girar a la ${lado}`}
                          onClick={() => setGiro(g => (g + paso + 360) % 360)}
                          style={{ padding:"4px 7px", borderRadius:7, border:"1px solid var(--border)",
                                   background:"var(--surface2)", cursor:"pointer", lineHeight:0 }}>
                          <Icon name="arrow-repeat" size={12} color="var(--text3)"
                            style={paso < 0 ? { transform:"scaleX(-1)" } : undefined}/>
                        </button>
                      ))}
                    </div>
                  )}

                  {activo && (
                    <button className="btn" onClick={() => descargar(activo)}
                      title={`Descargar ${activo.nombre}`}
                      style={{ marginLeft: activo?.imagen ? 0 : "auto", padding:"4px 10px", borderRadius:7,
                               border:"1px solid var(--border)", background:"var(--surface2)",
                               cursor:"pointer", fontSize:11, color:"var(--text2)",
                               fontFamily:"var(--font)", display:"flex", alignItems:"center", gap:5 }}>
                      <Icon name="download" size={11} color="var(--text3)"/>Descargar
                    </button>
                  )}
                </div>

                <div style={{ display:"flex", gap:7, overflowX:"auto", paddingBottom:3 }}>
                  {visibles.map(a => {
                    const sel = activo?.id === a.id;
                    return (
                      <div key={a.id} title={`${a.nombre} · ${tamano(a.bytes)}`}
                        onClick={() => (PREVISUALIZABLE(a) ? setActivo(a) : descargar(a))}
                        style={{ flex:"0 0 66px", height:66, borderRadius:8, cursor:"pointer",
                                 border:`2px solid ${sel ? "var(--accent)" : "var(--border)"}`,
                                 overflow:"hidden", background:"var(--bg)",
                                 display:"flex", alignItems:"center", justifyContent:"center" }}>
                        {a.imagen && urls[a.id]
                          ? <img src={urls[a.id]} alt={a.nombre}
                              style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                          : <Icon name={a.imagen ? "eye" : "file-text"} size={17} color="var(--text3)"/>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Ficha: el hilo hace su propio scroll y el compositor queda fijo
              abajo, para que responder este siempre a un clic sin bajar. */}
          <div style={{ flex:"1 1 50%", minWidth:0, display:"flex", flexDirection:"column", minHeight:0 }}>
          <div style={{ flex:1, minHeight:0, overflowY:"auto", padding:"16px 22px 18px" }}>

            {!hayVisor && firmas.length > 0 && (
              <button className="btn" onClick={() => setVerFirmas(true)}
                title="Las imágenes de firma se detectan por repetirse en muchas incidencias"
                style={{ marginBottom:12, padding:"4px 10px", borderRadius:7,
                         border:"1px solid var(--border)", background:"transparent",
                         cursor:"pointer", fontSize:10.5, fontFamily:"var(--font)",
                         color:"var(--text3)", display:"flex", alignItems:"center", gap:5 }}>
                <Icon name="eye" size={11} color="var(--text3)"/>
                Ver {firmas.length === 1 ? "la imagen de firma" : `las ${firmas.length} imágenes de firma`}
              </button>
            )}

            <div style={{ borderTop:"1px solid var(--border2)", paddingTop:14,
                          display: duplicada ? "none" : "block" }}>
              <div style={etiqueta}>Descripción</div>
              {cargando
                ? <div style={{ fontSize:12, color:"var(--text3)" }}>Cargando…</div>
                : ticket.descripcion
                  ? <>
                      <div style={{ fontSize:13, lineHeight:1.65, color:"var(--text2)", overflowWrap:"anywhere" }}
                        // El HTML llega saneado del backend: los tickets nacen de
                        // correos y pueden traer cualquier cosa.
                        dangerouslySetInnerHTML={{ __html: completos.desc && ticket.descripcion_completa
                                                          ? ticket.descripcion_completa : ticket.descripcion }}/>
                      {ticket.descripcion_completa && <VerOriginal clave="desc" activo={completos.desc}/>}
                    </>
                  : <div style={{ fontSize:12, color:"var(--text3)" }}>Sin descripción.</div>}
            </div>

            {/* Hilo del chatter de Odoo. La descripción es solo el primer
                correo: las respuestas posteriores viven en mail.message. */}
            {mensajes.length > 0 && (
              <div style={{ borderTop:"1px solid var(--border2)", paddingTop:14, marginTop:16 }}>
                <div style={{ ...etiqueta, marginBottom:10 }}>
                  Conversación · {mensajes.length} mensaje{mensajes.length === 1 ? "" : "s"}
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {mensajes.map(m => (
                    <div key={m.id}
                      style={{ border:`1px solid color-mix(in srgb, ${m.interno ? "#f59e0b" : "var(--accent)"} 45%, transparent)`,
                               background:"var(--surface2)", borderRadius:9, padding:"9px 12px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:5, flexWrap:"wrap" }}>
                        <Avatar name={m.autor} size={20}/>
                        <span style={{ fontSize:11.5, fontWeight:700, color:"var(--text)" }}>{m.autor}</span>
                        {m.interno && (
                          <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:20,
                                         background:"#f59e0b1f", color:"#f59e0b" }}>NOTA INTERNA</span>
                        )}
                        {m.adjuntos > 0 && (
                          <span style={{ fontSize:10, color:"var(--text3)", display:"flex",
                                         alignItems:"center", gap:3 }}>
                            <Icon name="clipboard" size={10} color="var(--text3)"/>{m.adjuntos}
                          </span>
                        )}
                        <span style={{ fontSize:10.5, color:"var(--text3)", marginLeft:"auto" }}>{fecha(m.fecha)}</span>
                      </div>
                      <div style={{ fontSize:12.5, lineHeight:1.6, color:"var(--text2)", overflowWrap:"anywhere" }}
                        dangerouslySetInnerHTML={{ __html: completos[m.id] && m.cuerpo_completo
                                                          ? m.cuerpo_completo : m.cuerpo }}/>
                      {m.cuerpo_completo && <VerOriginal clave={m.id} activo={completos[m.id]}/>}
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>

            {/* Responder. Fijo al pie y plegado mientras no se usa: ocupaba un
                tercio del panel estando casi siempre vacio. Los dos destinos van
                separados porque no hay vuelta atras: mail.mt_comment le llega al
                cliente, mail.mt_note no. */}
            {onResponder && (
              <div style={{ flexShrink:0, borderTop:"1px solid var(--border2)",
                            padding:"12px 22px 14px", background:"var(--surface)" }}>
                {!desplegado ? (
                  <div {...clicable(() => setDesplegado(true), { label: "Responder a esta incidencia" })}
                    style={{ display:"flex", alignItems:"center", gap:9, padding:"10px 13px",
                             borderRadius:9, border:"1px solid var(--border)",
                             background:"var(--surface2)", cursor:"text", fontSize:12.5,
                             color:"var(--text3)" }}>
                    <Icon name="chat-dots-fill" size={13} color="var(--text3)"/>
                    Responder a esta incidencia…
                  </div>
                ) : (
                  <>
                    <textarea aria-label="Mensaje"
                      autoFocus value={borrador} onChange={e => setBorrador(e.target.value)}
                      placeholder="Escribe tu mensaje…" rows={4} disabled={enviando}
                      style={{ width:"100%", boxSizing:"border-box", padding:"10px 12px", borderRadius:9,
                               border:"1px solid var(--border)", background:"var(--surface2)",
                               color:"var(--text)", fontSize:12.5, fontFamily:"var(--font)",
                               lineHeight:1.5, resize:"vertical" }}/>

                    <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap", alignItems:"center" }}>
                      <button className="btn" disabled={!borrador.trim() || enviando}
                        onClick={async () => {
                          const quien = ticket.contacto?.nombre || "el contacto de la incidencia";
                          if (!window.confirm(`Se enviara a ${quien} y no se puede retirar. Enviar?`)) return;
                          setEnviando(true);
                          const ok = await onResponder(borrador.trim(), false);
                          setEnviando(false);
                          if (ok) { setBorrador(""); setDesplegado(false); }
                        }}
                        style={{ padding:"8px 14px", borderRadius:9, border:"none", cursor:"pointer",
                                 fontSize:12, fontWeight:700, color:"var(--on-accent)", fontFamily:"var(--font)",
                                 background:"var(--accent)", opacity:(!borrador.trim() || enviando) ? .5 : 1,
                                 display:"flex", alignItems:"center", gap:6 }}>
                        <Icon name="chat-dots-fill" size={12} color="var(--on-accent)"/>
                        {enviando ? "Enviando…" : "Responder al cliente"}
                      </button>

                      <button className="btn" disabled={!borrador.trim() || enviando}
                        onClick={async () => {
                          setEnviando(true);
                          const ok = await onResponder(borrador.trim(), true);
                          setEnviando(false);
                          if (ok) { setBorrador(""); setDesplegado(false); }
                        }}
                        style={{ padding:"8px 14px", borderRadius:9, cursor:"pointer", fontSize:12,
                                 fontWeight:600, fontFamily:"var(--font)", color:"#f59e0b",
                                 border:"1px solid #f59e0b55", background:"#f59e0b14",
                                 opacity:(!borrador.trim() || enviando) ? .5 : 1,
                                 display:"flex", alignItems:"center", gap:6 }}>
                        <Icon name="pencil" size={12} color="#f59e0b"/>Nota interna
                      </button>

                      <button className="btn" onClick={() => { setBorrador(""); setDesplegado(false); }}
                        style={{ padding:"8px 12px", borderRadius:9, border:"1px solid var(--border)",
                                 background:"transparent", cursor:"pointer", fontSize:12,
                                 color:"var(--text3)", fontFamily:"var(--font)" }}>
                        Cancelar
                      </button>

                      <span style={{ fontSize:10.5, color:"var(--text3)" }}>
                        La nota interna no sale de la empresa.
                      </span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Se monta en document.body: dentro de la página, el contenedor animado con
  // `transform` (clase .fu) pasa a ser el bloque contenedor de cualquier
  // position:fixed, y por eso el fondo gris solo tapaba el área de contenido.
  return createPortal(contenido, document.body);
}

// Una entrada del menu del boton de tiempo. Cada una lleva su pie: las tres
// escriben en sitios distintos —solo el cronometro, el cuaderno, o el cuaderno y
// Odoo— y eso no se adivina del titulo.
function OpcionTiempo({ icono, color, titulo, pie, onClick, disabled }) {
  return (
    <button className="btn" onClick={onClick} disabled={disabled}
      style={{ display:"flex", alignItems:"flex-start", gap:9, width:"100%", textAlign:"left",
               padding:"7px 9px", borderRadius:8, border:"none", background:"transparent",
               cursor: disabled ? "default" : "pointer", fontFamily:"var(--font)",
               opacity: disabled ? .5 : 1 }}
      onMouseEnter={e => { e.currentTarget.style.background = "var(--surface2)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
      <Icon name={icono} size={14} color={color} style={{ marginTop:1, flexShrink:0 }}/>
      <span style={{ minWidth:0 }}>
        <span style={{ display:"block", fontSize:12, fontWeight:700, color:"var(--text)" }}>{titulo}</span>
        <span style={{ display:"block", fontSize:10.5, color:"var(--text3)", lineHeight:1.4 }}>{pie}</span>
      </span>
    </button>
  );
}
